import { CalendarDate, DefaultReferences, Maybe } from "@sp-crm/core";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getGroupedRowModel,
    getSortedRowModel,
    GroupingState,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { CalendarDateInput } from "components/ui/calendar-date";
import {
    ExpandDown,
    ExpandRight,
    GroupGrouped,
    GroupUngrouped,
    SortDown,
    SortUp,
} from "components/ui/icon";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ApplicationState } from "store/state";
import { ReferralsQuery, useReferralsQuery } from "../../../generated/graphql";
import { Actions } from "../../../store/actions";
import { ContentContainer, ContentHeader, PageHeader, Stage, Tile } from "../../layout";

export const Referrals: React.FunctionComponent = () => {
    const region = useSelector((state: ApplicationState) => state.region.selectedRegion);
    const dispatch = useDispatch();
    const endDateOnChangeHandler = (newdate: CalendarDate) => {
        dispatch({
            type: Actions[Actions.REPORT_PARAMETER_INBOUND_REFERRAL_END_DATE_UPDATE],
            value: newdate,
        });
    };

    const startDateOnChangeHandler = (newdate: CalendarDate) => {
        dispatch({
            type: Actions[Actions.REPORT_PARAMETER_INBOUND_REFERRAL_START_DATE_UPDATE],
            value: newdate,
        });
    };

    const startDate = useSelector(
        (state: ApplicationState) => state.reportParameters.inboundReferral.startDate,
    );

    const endDate = useSelector(
        (state: ApplicationState) => state.reportParameters.inboundReferral.endDate,
    );

    const validQuery = region && region !== "" && startDate && endDate;

    return (
        <Stage>
            <PageHeader title="Referrals">
                <div className="max-w-lg lg:max-w-none lg:flex justify-between lg:space-x-3 items-end">
                    <CalendarDateInput
                        label="Start"
                        value={startDate}
                        onChange={startDateOnChangeHandler}
                    />
                    <CalendarDateInput
                        label="End"
                        value={endDate}
                        onChange={endDateOnChangeHandler}
                    />
                </div>
            </PageHeader>
            {validQuery ? (
                <Query
                    startDate={startDate.toString()}
                    endDate={endDate.toString()}
                    region={region}
                />
            ) : (
                ""
            )}
        </Stage>
    );
};

const Query: React.FC<{
    startDate: string;
    endDate: string;
    region: string;
}> = props => {
    const queryResult = useReferralsQuery(props);

    return (
        <QueryRenderer name="Referrals.Query" query={queryResult}>
            {data => (
                <ContentContainer className="space-y-2">
                    <div className="grid grid-cols-1 w-full gap-0 lg:gap-2 lg:grid-cols-2">
                        <Tile>
                            <ContentHeader>Contacts</ContentHeader>
                            <ReferralsContactsPane data={data} />
                        </Tile>
                        <Tile>
                            <ContentHeader>Organizations</ContentHeader>
                            <ReferralsOrganizationPane data={data} />
                        </Tile>
                        <Tile>
                            <ContentHeader>General</ContentHeader>
                            <InboundReferralsGeneralPane data={data} />
                        </Tile>
                    </div>
                </ContentContainer>
            )}
        </QueryRenderer>
    );
};

interface GeneralReferral {
    referralToken: string;
    count: number;
}
const InboundReferralsGeneralPane: React.FC<{ data: ReferralsQuery }> = props => {
    const [sorting, setSorting] = React.useState<SortingState>([
        { id: "count", desc: true },
    ]);

    const columns: ColumnDef<GeneralReferral>[] = useMemo(
        () => [
            {
                accessorKey: "referralToken",
                header: "Source",
                cell: info =>
                    Maybe.fromValue(
                        DefaultReferences.find(r => r.key === info.getValue()),
                    )
                        .map(r => r.text)
                        .getOrElse("unknown"),
            },
            {
                accessorKey: "count",
                header: "Referrals",
            },
        ],
        [],
    );
    const data: GeneralReferral[] = useMemo(
        () => props.data.reportReferrals.generic,
        [props.data],
    );

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });
    return (
        <table className="w-full">
            <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                            <th
                                key={header.id}
                                className="py-1 px-2 bg-gray-200 text-gray-600 uppercase text-sm leading-normal"
                                onClick={header.column.getToggleSortingHandler()}>
                                <div className="flex items-center cursor-pointer">
                                    <span>
                                        {header.column.getIsSorted() ? (
                                            header.column.getIsSorted() === "desc" ? (
                                                <SortDown className="mr-1" />
                                            ) : (
                                                <SortUp className="mr-1" />
                                            )
                                        ) : (
                                            ""
                                        )}
                                    </span>
                                    <span>
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                        )}
                                    </span>
                                </div>
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>

            <tbody>
                {table.getRowModel().rows.map((row, i) => (
                    <tr
                        key={row.id}
                        className={`border-b border-gray-200 hover:bg-gray-100 ${
                            i % 2 === 1 ? "bg-gray-50" : "bg-white"
                        }`}>
                        {row.getVisibleCells().map(cell => (
                            <td key={cell.id} className="py-1 px-2">
                                {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext(),
                                )}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

interface OrganizationReferral {
    inboundCount: number;
    outboundCount: number;
    difference: number;
    name: string;
    contactType: string;
    id: string;
}
const ReferralsOrganizationPane: React.FC<{
    data: ReferralsQuery;
}> = props => {
    const [sorting, setSorting] = React.useState<SortingState>([
        { id: "inboundCount", desc: true },
    ]);
    const [grouping, setGrouping] = React.useState<GroupingState>(["contactType"]);
    const [expanded, setExpanded] = React.useState({});

    const columns: ColumnDef<OrganizationReferral>[] = useMemo(
        (): ColumnDef<OrganizationReferral>[] => [
            {
                accessorKey: "contactType",
                header: "Type",
                enableGrouping: true,
            },
            {
                accessorKey: "name",
                header: "Source",
                enableGrouping: false,
                cell: info => {
                    const row = info.row.original;
                    return row?.id ? (
                        <Link to={`/references/organizations/show/${row.id}`}>
                            {info.getValue() as string}
                        </Link>
                    ) : (
                        ""
                    );
                },
            },
            {
                accessorKey: "inboundCount",
                header: "Inbound",
                enableGrouping: false,
                aggregationFn: "sum",
            },
            {
                accessorKey: "outboundCount",
                header: "Outbound",
                enableGrouping: false,
                aggregationFn: "sum",
            },
            {
                accessorKey: "difference",
                header: "+/-",
                enableGrouping: false,
                aggregationFn: "sum",
            },
        ],
        [],
    );
    const data: OrganizationReferral[] = useMemo(
        () =>
            props.data.reportReferrals.businesses.map(b => {
                return {
                    inboundCount: b.inboundCount,
                    outboundCount: b.outboundCount,
                    difference: b.inboundCount - b.outboundCount,
                    contactType:
                        b.business.businessContactEntity.contactType ?? "(unknown)",
                    name: b.business.businessContactEntity.name,
                    id: b.business.id,
                };
            }),
        [props.data],
    );

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            grouping,
            expanded,
        },
        onSortingChange: setSorting,
        onGroupingChange: setGrouping,
        onExpandedChange: setExpanded,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getGroupedRowModel: getGroupedRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
    });

    React.useEffect(() => {
        table.toggleAllRowsExpanded(true);
    }, [table]);

    return (
        <table className="w-full">
            <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        <th className="w-4 py-1 px-2 bg-gray-200 text-gray-600 uppercase text-sm leading-normal"></th>
                        {headerGroup.headers.map(header => (
                            <th
                                key={header.id}
                                className="py-1 px-2 bg-gray-200 text-gray-600 uppercase text-sm leading-normal">
                                <div className="flex items-center">
                                    {header.column.getCanGroup() ? (
                                        <span
                                            className="cursor-pointer"
                                            onClick={header.column.getToggleGroupingHandler()}>
                                            {header.column.getIsGrouped() ? (
                                                <GroupGrouped className="mr-1" />
                                            ) : (
                                                <GroupUngrouped className="mr-1" />
                                            )}
                                        </span>
                                    ) : null}
                                    <span
                                        className="cursor-pointer"
                                        onClick={header.column.getToggleSortingHandler()}>
                                        {header.column.getIsSorted() ? (
                                            header.column.getIsSorted() === "desc" ? (
                                                <SortDown className="mr-1" />
                                            ) : (
                                                <SortUp className="mr-1" />
                                            )
                                        ) : (
                                            ""
                                        )}
                                    </span>
                                    <span
                                        className="cursor-pointer"
                                        onClick={header.column.getToggleSortingHandler()}>
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                        )}
                                    </span>
                                </div>
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>

            <tbody>
                {table.getRowModel().rows.map((row, i) => (
                    <tr
                        key={row.id}
                        className={`border-b border-gray-200 hover:bg-gray-100 ${
                            i % 2 === 1 ? "bg-gray-50" : "bg-white"
                        }`}>
                        <td className="w-4">
                            {row.getCanExpand() ? (
                                <span
                                    className="cursor-pointer"
                                    onClick={row.getToggleExpandedHandler()}>
                                    {row.getIsExpanded() ? (
                                        <ExpandDown />
                                    ) : (
                                        <ExpandRight />
                                    )}
                                </span>
                            ) : (
                                ""
                            )}
                        </td>
                        {row.getVisibleCells().map((cell, cellIndex) => (
                            <td
                                key={cell.id}
                                className={`py-1 ${
                                    row.getIsGrouped() ? "font-bold" : ""
                                } ${
                                    cellIndex === 0 && !row.getIsGrouped()
                                        ? "pl-4 pr-2"
                                        : "px-2"
                                }`}>
                                {cell.getIsAggregated()
                                    ? flexRender(
                                          cell.column.columnDef.aggregatedCell ??
                                              cell.column.columnDef.cell,
                                          cell.getContext(),
                                      )
                                    : flexRender(
                                          cell.column.columnDef.cell,
                                          cell.getContext(),
                                      )}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

interface ContactReferral {
    inboundCount: number;
    outboundCount: number;
    difference: number;
    name: string;
    id: string;
}
const ReferralsContactsPane: React.FC<{
    data: ReferralsQuery;
}> = props => {
    const [sorting, setSorting] = React.useState<SortingState>([
        { id: "inboundCount", desc: true },
    ]);

    const columns: ColumnDef<ContactReferral>[] = useMemo(
        () => [
            {
                accessorKey: "name",
                header: "Source",
                cell: info => {
                    const row = info.row.original;
                    return (
                        <Link to={`/references/contacts/show/${row.id}`}>
                            {info.getValue() as string}
                        </Link>
                    );
                },
            },
            {
                accessorKey: "inboundCount",
                header: "Inbound",
            },
            {
                accessorKey: "outboundCount",
                header: "Outbound",
            },
            {
                accessorKey: "difference",
                header: "+/-",
            },
        ],
        [],
    );
    const data: ContactReferral[] = useMemo(
        () =>
            props.data.reportReferrals.contacts.map(c => {
                return {
                    inboundCount: c.inboundCount,
                    outboundCount: c.outboundCount,
                    difference: c.inboundCount - c.outboundCount,
                    name: c.contact.contactEntity.name,
                    id: c.contact.id,
                };
            }),
        [props.data],
    );

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <table className="w-full">
            <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                            <th
                                key={header.id}
                                className="py-1 px-2 bg-gray-200 text-gray-600 uppercase text-sm leading-normal">
                                <div className="flex items-center">
                                    <span
                                        className="cursor-pointer"
                                        onClick={header.column.getToggleSortingHandler()}>
                                        {header.column.getIsSorted() ? (
                                            header.column.getIsSorted() === "desc" ? (
                                                <SortDown className="mr-1" />
                                            ) : (
                                                <SortUp className="mr-1" />
                                            )
                                        ) : (
                                            ""
                                        )}
                                    </span>
                                    <span
                                        className="cursor-pointer"
                                        onClick={header.column.getToggleSortingHandler()}>
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                        )}
                                    </span>
                                </div>
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody>
                {table.getRowModel().rows.map((row, i) => (
                    <tr
                        key={row.id}
                        className={`border-b border-gray-200 hover:bg-gray-100 ${
                            i % 2 === 1 ? "bg-gray-50" : "bg-white"
                        }`}>
                        {row.getVisibleCells().map(cell => (
                            <td key={cell.id} className="py-1 px-2">
                                {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext(),
                                )}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};
